import { Input } from '@teamsnap/snap-ui';
import { NativeSelectOption } from '@teamsnap/snap-ui/build/components/Input/Input.types';
import { PreFormEditedProperties } from 'core/ampli';
import { AMPLITUDE_PRE_EDIT, useAmplitude } from 'hooks/amplitude';
import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  usePostRegEditActions,
  usePostRegEditDrawer,
} from '../../../../../hooks/postRegEditDrawer/usePostRegEditDrawer';
import {
  FormQuestion,
  QuestionType,
  REGISTRATION_OPTION_LABEL,
  SelectOption,
} from '../types';
import { FileInput } from './FileInput';
import { GroupCheckboxInput } from './GroupCheckboxInput';

// Renders a small dropdown for selecting a quantity for a given option
export const QuantityInput = React.memo(
  ({
    maxQuantity,
    value,
    onChange,
    error,
  }: {
    maxQuantity: number;
    value: string;
    onChange: (number: string) => void;
    error: string;
  }) => {
    const generateRangeOptions = (max: number): NativeSelectOption[] => {
      const range: number[] = [];
      for (let i = 1; i <= max; i++) {
        range.push(i);
      }
      return range.map((value) => ({
        label: String(value),
        value: String(value),
      }));
    };

    return (
      <Input
        name="quantity"
        type={'select'}
        label={'Quantity'}
        className="sui-ml-4 sui-w-1/3"
        multiple={false}
        errors={error ? [error] : undefined}
        value={value}
        options={generateRangeOptions(maxQuantity)}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
);

// Renders a small input for entering an amount for a given option
export const AmountRangeInput = React.memo(
  ({
    value,
    onChange,
    error,
  }: {
    value: string;
    onChange: (number: string) => void;
    error: string;
  }) => (
    <Input
      name="amountRange"
      className="sui-ml-4 sui-w-1/3"
      type="financial"
      allowDecimals
      errors={error ? [error] : undefined}
      label="Amount"
      onChange={(e) => onChange(e.target.value)}
      value={value || ''}
    />
  )
);

const SelectInput = React.memo(
  ({
    question,
    trackUpdate,
  }: {
    question: FormQuestion;
    trackUpdate: () => void;
  }) => {
    const {
      formErrors,
      currentFormState: formState,
      dirtyFields,
    } = usePostRegEditDrawer();
    const { updateFormValue, updateFormDetails } = usePostRegEditActions();
    const { id, options, label, defaults } = question;
    const answers = formState[id];
    const formValue = answers[0]?.value || defaults[0]?.value;
    const [selectedOption, setSelectedOption] = useState<SelectOption>(
      question.options.find((option) => option.value === formValue)
    );

    useEffect(() => {
      if (selectedOption?.maxQuantity && !answers[0].details) {
        updateFormDetails(id, formValue, '1');
      }
    }, [selectedOption, formValue, id]);

    function onInputChange(e: React.ChangeEvent<HTMLInputElement>) {
      const newValue = e.target.value;
      const option = options.find((option) => option.value === newValue);
      setSelectedOption(option);
      updateFormValue(id, [newValue]);
      trackUpdate();
    }

    const hasError = formErrors[id]?.length > 0;
    let questionError: string, detailsError: string;
    if (hasError) {
      const { error, details } = formErrors[id][0]; // Select input will have only one entry in this array
      questionError = !details ? error : '';
      detailsError = details ? error : '';
    }

    return (
      <div className="sui-flex">
        <Input
          key={id}
          options={options}
          errors={questionError ? [questionError] : undefined}
          label={`${label} ${dirtyFields.includes(id) ? '(Edited)' : ''}`}
          onChange={onInputChange}
          value={formValue || ''}
          type="select"
          name={id}
        />
        {selectedOption?.amountRange && (
          <AmountRangeInput
            value={answers[0].details}
            error={detailsError}
            onChange={(quantity) => updateFormDetails(id, formValue, quantity)}
          />
        )}
        {selectedOption?.maxQuantity && (
          <QuantityInput
            maxQuantity={selectedOption.maxQuantity}
            value={answers[0].details || '1'}
            error={detailsError}
            onChange={(quantity) => updateFormDetails(id, formValue, quantity)}
          />
        )}
      </div>
    );
  }
);

interface Props {
  question: FormQuestion;
}

export const FormQuestionInput = React.memo(({ question }: Props) => {
  const { id, type, label } = question;
  const { currentFormState, dirtyFields, formErrors, formStructure } =
    usePostRegEditDrawer();
  const { amplitudeTrack } = useAmplitude();
  const { updateFormValue } = usePostRegEditActions();
  const trackUpdate = () => {
    let sectionName = AMPLITUDE_PRE_EDIT.ADDITIONAL_QUESTION;
    if (label === REGISTRATION_OPTION_LABEL) {
      sectionName = AMPLITUDE_PRE_EDIT.REGISTRATION_OPTION;
    } else if (formStructure.feeRelatedQuestions.some((q) => q.id === id)) {
      sectionName = AMPLITUDE_PRE_EDIT.FEE_RELATED_QUESTION;
    }
    amplitudeTrack.preFormEdited(
      sectionName as PreFormEditedProperties['pre_edit']
    );
  };

  if (!currentFormState[id]) {
    return null;
  }

  const questionLabel = dirtyFields.includes(id) ? `${label} (Edited)` : label;
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    updateFormValue(id, [value]);
  };
  const onBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    updateFormValue(id, [value]);
    trackUpdate(); // for fields that are typed in, only need one event
  };
  const formValue = currentFormState[id][0]?.value || '';
  const commonProps = {
    onChange,
    label: questionLabel,
    errors: formErrors[id]?.length
      ? formErrors[id].map((err) => err.error)
      : undefined,
    name: id,
    value: formValue,
    onBlur,
  };

  switch (type) {
    case QuestionType.FILE:
      return <FileInput question={question} />;
    case QuestionType.EMAIL:
      return <Input type="email" {...commonProps} />;
    case QuestionType.TEXT:
      return <Input type="text" {...commonProps} />;
    case QuestionType.PHONE:
      return <Input type="phone" {...commonProps} />;
    case QuestionType.TEXTAREA:
      return <Input type="textarea" {...commonProps} />;
    case QuestionType.DATE:
      return (
        <Input
          type="date"
          {...{
            ...commonProps,
            value: formValue
              ? new Date(formValue).toISOString().split('T')[0]
              : '',
          }}
        />
      );
    case QuestionType.MULTISELECT:
      return (
        <GroupCheckboxInput question={question} trackUpdate={trackUpdate} />
      );
    case QuestionType.SELECT:
    default:
      return <SelectInput question={question} trackUpdate={trackUpdate} />;
  }
});
